'use client';

import React from 'react';
import Link from 'next/link';
import { CheckCircle, Circle, ChevronRight, X } from 'lucide-react';
import { ORIENTATION_LESSONS } from './orientationData';

interface OrientationSidebarProps {
  activeId: string;
  completedIds: Set<string>;
  onLessonClick: (id: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function OrientationSidebar({
  activeId,
  completedIds,
  onLessonClick,
  isOpen,
  onClose,
}: OrientationSidebarProps) {
  return (
    <aside
      className={`
        fixed lg:sticky top-0 lg:top-16 left-0 bottom-0 z-40
        w-72 xl:w-80 flex-shrink-0
        flex flex-col
        transition-transform duration-300 lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:h-[calc(100vh-64px)] overflow-y-auto
      `}
      style={{ backgroundColor: '#F9F7F4', borderRight: '1px solid #E5E0D8' }}
      aria-label="Orientation lesson navigation"
    >
      {/* Sidebar header */}
      <div
        className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0"
        style={{ borderColor: '#E5E0D8', backgroundColor: '#F9F7F4' }}
      >
        <div>
          <p
            className="font-body text-xs uppercase tracking-widest font-semibold mb-0.5"
            style={{ color: '#D4891A', letterSpacing: '0.12em' }}
          >
            Start Here
          </p>
          <h2 className="font-display font-semibold text-base" style={{ color: '#1B2A4A' }}>
            Orientation
          </h2>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-lg transition-colors duration-150"
          style={{ color: '#9CA3AF' }}
          aria-label="Close navigation"
        >
          <X size={18} />
        </button>
      </div>

      {/* Orientation lessons */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto" aria-label="Orientation lessons">
        {/* START HERE label */}
        <div className="px-2 pb-2">
          <span
            className="font-body text-xs uppercase tracking-widest font-semibold"
            style={{ color: '#D4891A', letterSpacing: '0.14em' }}
          >
            Start Here
          </span>
        </div>

        {/* O1–O4 */}
        <div className="flex flex-col gap-0.5 mb-5">
          {ORIENTATION_LESSONS.map((lesson) => {
            const isActive = activeId === lesson.id;
            const isComplete = completedIds.has(lesson.id);

            return (
              <button
                key={lesson.id}
                onClick={() => onLessonClick(lesson.id)}
                className="w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all duration-150 group"
                style={{
                  backgroundColor: isActive ? '#1B2A4A' : 'transparent',
                }}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                  {isComplete ? (
                    <CheckCircle
                      size={14}
                      style={{ color: isActive ? 'rgba(255,255,255,0.6)' : '#D4891A' }}
                      aria-hidden="true"
                    />
                  ) : isActive ? (
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: '#D4891A' }}
                      aria-hidden="true"
                    />
                  ) : (
                    <Circle size={12} style={{ color: '#C0C0C0' }} aria-hidden="true" />
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block font-body text-xs mb-0.5"
                    style={{ color: isActive ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}
                  >
                    {lesson.slug}
                  </span>
                  <span
                    className="block font-body text-sm font-medium leading-tight truncate"
                    style={{ color: isActive ? 'white' : '#1B2A4A' }}
                  >
                    {lesson.title}
                  </span>
                </span>
                {!isActive && (
                  <ChevronRight
                    size={14}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                    style={{ color: '#9CA3AF' }}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Divider before Module 1 */}
        <div
          className="mx-2 mb-4"
          style={{ height: '1px', backgroundColor: '#E5E0D8' }}
          aria-hidden="true"
        />

        {/* Module 1 link */}
        <div className="px-2 pb-2">
          <span
            className="font-body text-xs uppercase tracking-widest font-semibold"
            style={{ color: '#9CA3AF', letterSpacing: '0.12em' }}
          >
            Module 1
          </span>
        </div>
        <Link
          href="/module/1"
          className="w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all duration-150 group"
          style={{ color: '#1B2A4A' }}
        >
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            <Circle size={12} style={{ color: '#C0C0C0' }} aria-hidden="true" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-body text-xs mb-0.5" style={{ color: '#9CA3AF' }}>
              Start here after orientation
            </span>
            <span className="block font-body text-sm font-medium leading-tight" style={{ color: '#1B2A4A' }}>
              Copilot at Work
            </span>
          </span>
          <ChevronRight
            size={14}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ color: '#9CA3AF' }}
            aria-hidden="true"
          />
        </Link>
      </nav>

      {/* Progress indicator */}
      <div
        className="px-5 py-3 border-t flex-shrink-0"
        style={{ borderColor: '#E5E0D8' }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-body text-xs" style={{ color: '#9CA3AF' }}>
            {completedIds.size} of 4 orientation lessons
          </span>
          <span className="font-body text-xs font-semibold" style={{ color: '#D4891A' }}>
            {Math.round((completedIds.size / 4) * 100)}%
          </span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E0D8' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.round((completedIds.size / 4) * 100)}%`, backgroundColor: '#D4891A' }}
            role="progressbar"
            aria-valuenow={Math.round((completedIds.size / 4) * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
    </aside>
  );
}
